import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SaveDataState } from '../../redux/reducers/getSaveData/saveDataReducer'
import { ProfileOverviewState } from '../../redux/reducers/getProfile/getProfileOverviewReducer'
import RoundsRateData from '../../types/roundsRate/RoundsRateData'
import updateProperties from '../../utils/DataModifier/DataModifier'
import { useTranslation } from 'react-i18next'
import { getProfileAttributes, putProfileAttributes, resetGetProfileAttributes } from '../../redux/actions/profile/profile'
import { SaveResultState } from '../../redux/reducers/getSaveData/saveResultReducer'
import { ProfileAttributesState } from '../../redux/reducers/getProfile/profileAttributes/getAttributesReducer'
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion';
import AccordionItem, { DefaultAccordionProviderOptions } from '../../components/common/AccordionItem/AccordionItem';
import { useNavigate } from 'react-router-dom'
import { commaSeparate, formatCurrency } from '../../utils/formatter'
import i18n from '../../i18n'
import { useFormErrorContext } from '../../utils/context/FormErrorContext'
import { useNavigation } from '../../utils/context/NavigationContext'
import ErrorMessages from '../../components/common/ErrorMessages/ErrorMessages'
import { selectProfileGetAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../redux/selectors/exportSelectorPaths'
import RoundsRateTab from './RoundsRateTab'
import PreviousCampaignIncomeAttributes from '../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes'
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from '../../utils/previousIncomeAttributes'
import { GET_PREVIOUS_PROFILE_ATTRIBUTES } from '../../redux/actionTypes/actionsTypes'

export interface RoundsRateProps {
    readonly?: boolean;
    profileAttributes?: Record<string, number | null>;
}

function RoundsRate(props: RoundsRateProps) {
    const initialRoundsRateData: RoundsRateData = {
        "courseName1": null,
        "numberOfHole1": null,
        "primeRate1": null,
        "regularRound1": null,
        "memberRound1": null,
        "complimentaryRound1": null,
        "courseName2": null,
        "numberOfHole2": null,
        "primeRate2": null,
        "regularRound2": null,
        "memberRound2": null,
        "complimentaryRound2": null,
        "courseName3": null,
        "numberOfHole3": null,
        "primeRate3": null,
        "regularRound3": null,
        "memberRound3": null,
        "complimentaryRound3": null,
        "courseName4": null,
        "numberOfHole4": null,
        "primeRate4": null,
        "regularRound4": null,
        "memberRound4": null,
        "complimentaryRound4": null,
        "courseName5": null,
        "numberOfHole5": null,
        "primeRate5": null,
        "regularRound5": null,
        "memberRound5": null,
        "complimentaryRound5": null
    }
    const courses = [1, 2, 3, 4, 5]

    const { t } = useTranslation('rounds_rate')
    const dispatch = useDispatch()
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const profileAttributes: ProfileAttributesState = useSelector(selectProfileGetAttributes)
    const saveData: SaveDataState = useSelector(selectSaveData)
    const saveResultState: SaveResultState = useSelector(selectSaveResult)
    const [roundsRateData, setRoundsRateData] = useState<RoundsRateData>(initialRoundsRateData as RoundsRateData)
    const roundRatesKeys = Object.keys(initialRoundsRateData)
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const [displaySummaryAccordion, setDisplaySummaryAccordion] = useState(false);
    const navigate = useNavigate()
    const { direction } = useNavigation()
    const { clearErrors, errors } = useFormErrorContext()
    const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"

    useEffect(() => {
        const handleKeyDown = (e: { key: string }) => {
            if (e.key === "Backspace") {
                if (document.activeElement?.tagName !== "INPUT") {
                    const backButton = document.getElementById("back-button")
                    backButton?.click()
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [navigate])

    useEffect(() => {
        if (!props.profileAttributes) {
            dispatch(resetGetProfileAttributes())
            dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, Number(sessionStorage.getItem("currentSnapshotId") as string)))

            const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

            if (campaign) {
                dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, campaign.currentSnapshotId, GET_PREVIOUS_PROFILE_ATTRIBUTES))
            }
        } else {
            courses.forEach((e) => {
                if (props.profileAttributes && (props.profileAttributes[("courseName" + e) as keyof RoundsRateData] || props.profileAttributes[("numberOfHole" + e) as keyof RoundsRateData] || props.profileAttributes[("primeRate" + e) as keyof RoundsRateData]
                    || props.profileAttributes[("regularRound" + e) as keyof RoundsRateData] || props.profileAttributes[("memberRound" + e) as keyof RoundsRateData] || props.profileAttributes[("complimentaryRound" + e) as keyof RoundsRateData])) {
                    setDisplaySummaryAccordion(true);
                }
            })
            const initData = { ...initialRoundsRateData }
            updateProperties(initData, props.profileAttributes)
            setRoundsRateData(initData)
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (!props.profileAttributes && !profileAttributes.loading && profileAttributes.data) {
            const initData = { ...initialRoundsRateData }
            updateProperties(initData, profileAttributes.data)
            setRoundsRateData(initData)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileAttributes.data, profileAttributes.loading])

    const handleRoundsRateChange = (value: string | number | null | undefined, name: string) => {
        const na = (value === undefined || value === null || String(value) === "") ? null : value
        setRoundsRateData({ ...roundsRateData, [name]: na })
    }

    useEffect(() => {
        if (saveData.saving && !props.readonly) {
            clearErrors()
            const triggerSpectral = isInternal || direction !== "backward"

            const data = { ...profileAttributes.data, ...roundsRateData } as Record<string, unknown>
            dispatch(putProfileAttributes(profileOverview.profile.profile_type_code, Number(sessionStorage.getItem("currentSnapshotId") as string), {
                "profile_attributes": data,
                "spectral_section": "rounds_rate",
                "trigger_spectral": triggerSpectral,
                "is_internal": isInternal
            }))
        }
    }, [dispatch, profileAttributes.data, profileOverview.profile.profile_type_code, roundsRateData, saveData.saving, props.readonly, direction, clearErrors, isInternal])

    useEffect(() => {
        if (saveResultState.status === 200) {
            dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, Number(sessionStorage.getItem("currentSnapshotId") as string)))
        }
    }, [dispatch, profileOverview.profile.profile_type_code, saveResultState.status])

    return (
        <>
            {props.readonly ?
                <ControlledAccordion providerValue={providerValue}>
                    <AccordionItem
                        header={t("roundsTableHeader")}
                        key="roundsTable"
                        itemKey="roundsTable">
                        {displaySummaryAccordion ? <div className="summary-grid">
                            {courses.map((e, i) => {
                                return (
                                    (roundsRateData[("courseName" + e) as keyof RoundsRateData] || roundsRateData[("numberOfHole" + e) as keyof RoundsRateData] || roundsRateData[("primeRate" + e) as keyof RoundsRateData]
                                        || roundsRateData[("regularRound" + e) as keyof RoundsRateData] || roundsRateData[("memberRound" + e) as keyof RoundsRateData] || roundsRateData[("complimentaryRound" + e) as keyof RoundsRateData])
                                        ? <div className='summary-section-container' key={`rounds-rate-section-${e}`}><table className='summary-table' role='presentation'>
                                            <thead>
                                                <tr key={`rounds-rate-section-${e}-header`}>
                                                    <th>{t("course" + (i + 1))}</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {roundRatesKeys.map((key, index) => {
                                                    if (["courseName" + e, "numberOfHole" + e, "primeRate" + e, "regularRound" + e, "memberRound" + e, "complimentaryRound" + e].includes(key)) {
                                                        return (<tr key={`rounds-rate-section-${e}-${index}`}>
                                                            <td>{t(`headers.${key.substring(0, key.length - 1)}`)}</td>
                                                            <td>{key === ("primeRate" + e) ?
                                                                (roundsRateData[key as keyof RoundsRateData] ? "$ " + formatCurrency(roundsRateData[key as keyof RoundsRateData] as number, i18n.language) : "")
                                                                : commaSeparate(roundsRateData[key as keyof RoundsRateData])}</td>
                                                        </tr>)
                                                    } else return <></>
                                                })}
                                            </tbody>
                                        </table></div> : <></>)

                            })}
                        </div> : <></>}
                    </AccordionItem>
                </ControlledAccordion>
                :
                <div className='full-width'>
                    <div className="content-container">
                        <ErrorMessages errors={errors} />
                        <p className='strong'>{t('caption')}</p>
                        <div className='accordion-root'>
                            <div className={accordionDisplay(isInternal, "accordion")}>
                                <ControlledAccordion providerValue={providerValue}>
                                    <RoundsRateTab
                                        courses={courses}
                                        keys={roundRatesKeys}
                                        data={roundsRateData as Record<string, string | number | null>}
                                        valueChangeHandler={handleRoundsRateChange} />
                                </ControlledAccordion>
                            </div>
                            {shouldDisplayPreviousYearAttributes() ? <PreviousCampaignIncomeAttributes tabComponent='roundsRate' keys={roundRatesKeys} courses={courses} year={String(getCurrentCampaignYear() - 1)} /> : <></>}
                            {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <PreviousCampaignIncomeAttributes tabComponent='roundsRate' keys={roundRatesKeys} courses={courses} year={String(getCurrentCampaignYear() - 2)} /> : <></>}
                        </div>
                    </div>
                </div>
            }
        </>
    )
}

export default RoundsRate